import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Pencil, Plus, Save, Trash2, X, FileSpreadsheet } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { api, downloadFile, fmtDate } from "../../lib/api";
import { OptionList, VERTICAL_KEYS } from "../site/Primitives";
import { SessionStatusPill } from "../pro/ClientAcademyRequests";

const STATUSES = ["new", "in_progress", "scheduled", "done", "cancelled"];
const TEXT = ["title_fr", "title_en", "desc_fr", "desc_en", "objectives_fr", "objectives_en", "audience_fr", "audience_en", "duration_fr", "duration_en"];
const LABEL = { title_fr: "titleFr", title_en: "titleEn", desc_fr: "descFr", desc_en: "descEn", objectives_fr: "objFr", objectives_en: "objEn",
  audience_fr: "audFr", audience_en: "audEn", duration_fr: "durFr", duration_en: "durEn" };
const MULTI = ["desc_fr", "desc_en", "objectives_fr", "objectives_en"];
const BLANK = { vertical: "agro", format: "onsite", level: "beginner", standard: "", published: true, order: 0, ...Object.fromEntries(TEXT.map((k) => [k, ""])) };

const toForm = (m) => ({ ...BLANK, ...m, objectives_fr: (m.objectives_fr || []).join("\n"), objectives_en: (m.objectives_en || []).join("\n") });

const ModuleEditor = ({ initial, onSaved, onCancel }) => {
  const { t } = useLang();
  const ad = t.academy.admin;
  const [v, setV] = useState(toForm(initial));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...v, objectives_fr: v.objectives_fr.split("\n"), objectives_en: v.objectives_en.split("\n") };
    delete payload.id; delete payload.created_at; delete payload.updated_at;
    try {
      if (initial.id) await api.put(`/admin/academy/modules/${initial.id}`, payload);
      else await api.post("/admin/academy/modules", payload);
      toast.success(ad.saved); onSaved();
    } catch { toast.error(t.errors.fixErrors); }
    setBusy(false);
  };
  const sel = (k, map) => (
    <div className="f-field"><label htmlFor={`mod-${k}`}>{{ vertical: ad.vertical, format: t.academy.format, level: t.academy.level }[k]}</label>
      <select id={`mod-${k}`} className="f-input" value={v[k]} onChange={set(k)} data-testid={`admin-module-${k}-select`}><OptionList map={map} /></select></div>
  );
  return (
    <form className="panel ac-editor" onSubmit={save} data-testid="admin-module-editor">
      <div className="f-grid">
        {sel("vertical", Object.fromEntries(VERTICAL_KEYS.map((k) => [k, t.verticals[k]])))}
        <div className="f-field"><label htmlFor="mod-standard">{t.academy.standard}</label>
          <input id="mod-standard" className="f-input" value={v.standard} onChange={set("standard")} data-testid="admin-module-standard-input" /></div>
        {sel("format", t.academy.formats)}
        {sel("level", t.academy.levels)}
        {TEXT.map((k) => (
          <div className={`f-field${MULTI.includes(k) ? " f-span2-sm" : ""}`} key={k}>
            <label htmlFor={`mod-${k}`}>{ad[LABEL[k]]}</label>
            {MULTI.includes(k)
              ? <textarea id={`mod-${k}`} rows={3} className="f-input" value={v[k]} onChange={set(k)} data-testid={`admin-module-${k}-input`} />
              : <input id={`mod-${k}`} className="f-input" value={v[k]} onChange={set(k)} data-testid={`admin-module-${k}-input`} />}
          </div>
        ))}
      </div>
      <div className="f-actions">
        <button type="button" className="btn btn-ghost" onClick={onCancel} data-testid="admin-module-cancel-btn"><X aria-hidden="true" />{ad.cancel}</button>
        <button type="submit" className="btn btn-primary" disabled={busy} data-testid="admin-module-save-btn"><Save aria-hidden="true" />{ad.save}</button>
      </div>
    </form>
  );
};

const ModuleRow = ({ m, first, last, onEdit, act }) => {
  const { t, lang } = useLang();
  const ad = t.academy.admin;
  return (
    <tr data-testid={`admin-module-row-${m.id}`}>
      <td>{t.verticalsShort[m.vertical]}</td>
      <td><span className="strong">{lang === "en" ? m.title_en : m.title_fr}</span><br /><small style={{ color: "var(--text-2)" }}>{m.standard} · {t.academy.formats[m.format]} · {t.academy.levels[m.level]}</small></td>
      <td>
        <button type="button" role="switch" className="toggle" aria-checked={m.published} aria-label={`${ad.published} — ${m.title_fr}`}
          onClick={() => act(() => api.patch(`/admin/academy/modules/${m.id}/publish`, { published: !m.published }))} data-testid={`admin-module-publish-toggle-${m.id}`} />
      </td>
      <td>
        <div className="row-actions">
          <button type="button" className="btn btn-ghost btn-sm" disabled={first} aria-label={ad.up} onClick={() => act(() => api.post(`/admin/academy/modules/${m.id}/move`, { direction: "up" }))} data-testid={`admin-module-up-btn-${m.id}`}><ArrowUp aria-hidden="true" /></button>
          <button type="button" className="btn btn-ghost btn-sm" disabled={last} aria-label={ad.down} onClick={() => act(() => api.post(`/admin/academy/modules/${m.id}/move`, { direction: "down" }))} data-testid={`admin-module-down-btn-${m.id}`}><ArrowDown aria-hidden="true" /></button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onEdit(m)} data-testid={`admin-module-edit-btn-${m.id}`}><Pencil aria-hidden="true" />{ad.edit}</button>
          <button type="button" className="btn btn-danger btn-sm" onClick={() => window.confirm(ad.confirmDel(m.title_fr)) && act(() => api.delete(`/admin/academy/modules/${m.id}`))} data-testid={`admin-module-delete-btn-${m.id}`}><Trash2 aria-hidden="true" />{ad.del}</button>
        </div>
      </td>
    </tr>
  );
};

const RequestsTable = ({ rows, act }) => {
  const { t, lang } = useLang();
  const a = t.academy;
  if (!rows.length) return <p className="empty" data-testid="admin-session-requests-empty">{a.admin.requestsEmpty}</p>;
  return (
    <div className="table-wrap"><table className="data-table" data-testid="admin-session-requests-table" style={{ minWidth: 1100 }}>
      <thead><tr>{["ref", "date", "module", "company", "contact", "participants", "dates", "format", "emails", "status"].map((k) => <th key={k}>{a.h[k]}</th>)}</tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.id} data-testid={`admin-session-row-${r.ref}`}>
          <td className="mono strong">{r.ref}</td>
          <td className="mono">{fmtDate(r.created_at, lang)}</td>
          <td>{lang === "en" ? r.module_title_en : r.module_title_fr}<br /><small style={{ color: "var(--text-2)" }}>{t.verticalsShort[r.vertical]}</small></td>
          <td>{r.company}<br /><small style={{ color: "var(--text-2)" }}>{r.site}</small></td>
          <td>{r.contact}<br /><small style={{ color: "var(--text-2)" }}>{r.email}{r.phone ? ` · ${r.phone}` : ""}</small>{r.message && <><br /><small title={r.message}>{r.message.slice(0, 60)}</small></>}</td>
          <td className="mono">{r.participants}</td>
          <td>{r.preferred_dates}</td>
          <td>{a.formats[r.format]}</td>
          <td className="mono" style={{ fontSize: 11 }}>admin : {t.adminX.emailStatus[r.email_status] || r.email_status}<br />{t.adminX.confirmation} : {t.adminX.emailStatus[r.confirmation_status] || r.confirmation_status}</td>
          <td>
            <SessionStatusPill status={r.status} />
            <select className="f-input status-select" style={{ marginTop: 6 }} value={r.status} aria-label={`${a.h.status} ${r.ref}`}
              onChange={(e) => act(() => api.patch(`/admin/academy/requests/${r.id}`, { status: e.target.value }))} data-testid={`admin-session-status-select-${r.ref}`}>
              {STATUSES.map((s) => <option key={s} value={s}>{a.statuses[s]}</option>)}
            </select>
          </td>
        </tr>
      ))}</tbody>
    </table></div>
  );
};

export const AdminAcademy = () => {
  const { t } = useLang();
  const ad = t.academy.admin;
  const [data, setData] = useState({ modules: null, requests: [] });
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    const [modules, requests] = await Promise.all(["/admin/academy/modules", "/admin/academy/requests"].map((u) => api.get(u).then((r) => r.data)));
    setData({ modules, requests });
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn) => { try { await fn(); toast.success(t.admin.updated); load(); } catch { toast.error(t.errors.generic); } };

  if (!data.modules) return null;
  return (
    <div data-testid="admin-academy-panel">
      <div className="sub-head">
        <h3 className="sub-title">{ad.requests} ({data.requests.length})</h3>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => downloadFile("/admin/backup/csv/academy_requests", "academy-requests.csv").catch(() => toast.error(t.errors.generic))} data-testid="admin-session-requests-csv-btn">
          <FileSpreadsheet aria-hidden="true" />{ad.csv}
        </button>
      </div>
      <div style={{ marginTop: 14 }}><RequestsTable rows={data.requests} act={act} /></div>
      <div className="settings-block">
        <div className="sub-head">
          <h3 className="sub-title">{ad.modules} ({data.modules.length})</h3>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing(BLANK)} data-testid="admin-module-add-btn"><Plus aria-hidden="true" />{ad.add}</button>
        </div>
        {editing && <ModuleEditor key={editing.id || "new"} initial={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        {!data.modules.length ? <p className="empty">{ad.empty}</p> : (
          <div className="table-wrap" style={{ marginTop: 14 }}><table className="data-table" data-testid="admin-modules-table" style={{ minWidth: 820 }}>
            <thead><tr><th>{ad.vertical}</th><th>{t.academy.h.module}</th><th>{ad.published}</th><th>{t.admin.h.actions}</th></tr></thead>
            <tbody>{data.modules.map((m, i, arr) => (
              <ModuleRow key={m.id} m={m} onEdit={setEditing} act={act}
                first={i === 0 || arr[i - 1].vertical !== m.vertical} last={i === arr.length - 1 || arr[i + 1].vertical !== m.vertical} />
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
};
